// home/CTASection.jsx — Security Readiness Audit
import React, { useState } from 'react';

const SECTIONS = [
  {
    id: "facility",
    label: "Facility Profile",
    icon: "🏢",
    questions: [
      { id: "f1", text: "What type of facility do you operate?", type: "single", options: ["Corporate Office", "Warehouse / Distribution", "Retail / Multi-Tenant", "Healthcare / Hospital", "Education / Campus", "Government / Critical Infrastructure", "Manufacturing / Industrial", "Mixed-Use / Other"] },
      { id: "f2", text: "How many physical locations require security coverage?", type: "single", options: ["1", "2–5", "6–20", "21–50", "50+"] },
      { id: "f3", text: "What is the approximate total square footage across all locations?", type: "single", options: ["Under 10,000 sq ft", "10,000–50,000 sq ft", "50,000–200,000 sq ft", "200,000–1M sq ft", "Over 1M sq ft"] },
      { id: "f4", text: "How many entry/exit points does your primary facility have?", type: "single", options: ["1–5", "6–15", "16–30", "30+"] },
      { id: "f5", text: "What are your peak operating hours?", type: "single", options: ["Standard business (9–5)", "Extended hours (6am–10pm)", "24/7 operations", "Variable / seasonal shifts"] },
    ],
  },
  {
    id: "posture",
    label: "Current Security Posture",
    icon: "🛡",
    questions: [
      { id: "p1", text: "How many security personnel are currently on staff (all shifts combined)?", type: "single", options: ["0 (no dedicated staff)", "1–5", "6–15", "16–30", "30+"] },
      { id: "p2", text: "What is your current monitoring model?", type: "single", options: ["No active monitoring", "Guards do periodic walkthroughs", "Live camera monitoring by on-site staff", "Remote video monitoring (3rd party SOC)", "Hybrid (on-site + remote)", "AI-assisted monitoring"] },
      { id: "p3", text: "How would you rate your team's current alert response time?", type: "single", options: ["Under 30 seconds", "30 seconds – 2 minutes", "2–5 minutes", "5–15 minutes", "Over 15 minutes / inconsistent", "We don't measure this"] },
      { id: "p4", text: "Which of these security challenges apply to your operation?", type: "multi", options: ["Alert fatigue / too many false alarms", "Guards watching empty areas for long stretches", "Slow or inconsistent incident response", "High guard turnover", "Blind spots in camera coverage", "Difficulty scaling across locations", "No unified dashboard or reporting", "Compliance or audit gaps"] },
      { id: "p5", text: "How satisfied are you with your current security vendor/system?", type: "single", options: ["Very satisfied", "Somewhat satisfied", "Neutral", "Somewhat dissatisfied", "Very dissatisfied", "We don't have a vendor"] },
    ],
  },
  {
    id: "technology",
    label: "Technology & Infrastructure",
    icon: "📡",
    questions: [
      { id: "t1", text: "How many cameras are currently deployed across all locations?", type: "single", options: ["None", "1–20", "21–50", "51–150", "150–500", "500+"] },
      { id: "t2", text: "What is the average age of your camera hardware?", type: "single", options: ["Less than 2 years", "2–5 years", "5–10 years", "Over 10 years", "Mixed / unknown"] },
      { id: "t3", text: "Which technologies are currently in use?", type: "multi", options: ["Analog CCTV cameras", "IP / network cameras", "Access control (badge/fob)", "Motion sensors / PIR detectors", "Video Management System (VMS)", "Cloud-based video storage", "License plate recognition (LPR)", "AI / analytics-based detection", "Intercom / two-way audio", "Alarm / intrusion panel"] },
      { id: "t4", text: "Do you currently use any AI or analytics for threat detection?", type: "single", options: ["Yes — actively deployed", "Piloting / evaluating", "Interested but haven't started", "No — not a priority", "Unsure what's available"] },
      { id: "t5", text: "How is your video footage currently stored?", type: "single", options: ["On-premise NVR/DVR only", "Cloud-only storage", "Hybrid (on-premise + cloud)", "No recording / live-only", "Unsure"] },
    ],
  },
  {
    id: "incidents",
    label: "Incident History & Risk",
    icon: "🔴",
    questions: [
      { id: "i1", text: "How many security incidents occurred at your facilities in the past 12 months?", type: "single", options: ["0", "1–3", "4–10", "11–25", "25+", "We don't track this"] },
      { id: "i2", text: "What types of incidents are most common?", type: "multi", options: ["Unauthorized access / tailgating", "Theft (internal or external)", "Vandalism / property damage", "Trespassing / loitering", "Workplace violence or threats", "Vehicle-related incidents", "Package / delivery theft", "Safety / compliance violations", "Slip-and-fall / liability events", "None — no incidents recorded"] },
      { id: "i3", text: "What was the estimated financial impact of security incidents last year?", type: "single", options: ["Under $5,000", "$5,000–$25,000", "$25,000–$100,000", "$100,000–$500,000", "Over $500,000", "Unknown / not measured"] },
      { id: "i4", text: "How are incidents typically discovered?", type: "single", options: ["Real-time alert from security system", "Guard or employee witnesses it", "Reviewing footage after the fact", "Third-party report (police, customer, etc.)", "Often goes undetected for hours or days"] },
      { id: "i5", text: "Have you experienced any incidents that resulted in legal action or insurance claims?", type: "single", options: ["Yes — multiple", "Yes — one", "No", "Prefer not to answer"] },
    ],
  },
  {
    id: "budget",
    label: "Budget & Objectives",
    icon: "💰",
    questions: [
      { id: "b1", text: "What is your approximate annual security budget (all-in)?", type: "single", options: ["Under $50,000", "$50,000–$150,000", "$150,000–$500,000", "$500,000–$1M", "Over $1M", "Prefer not to say"] },
      { id: "b2", text: "What percentage of your security budget goes to labor (guards, SOC staff)?", type: "single", options: ["Under 25%", "25–50%", "50–75%", "Over 75%", "Unsure"] },
      { id: "b3", text: "What are your top priorities for improving security? (Select up to 3)", type: "multi", maxSelect: 3, options: ["Reduce false alarms and alert fatigue", "Cut labor costs without reducing coverage", "Faster incident detection and response", "Better analytics and reporting", "Scalability across locations", "Compliance and audit readiness", "Reduce insurance premiums", "Modernize aging camera infrastructure"] },
      { id: "b4", text: "What is your timeline for making changes to your security operations?", type: "single", options: ["Immediately (within 30 days)", "This quarter (1–3 months)", "This half (3–6 months)", "This year", "Exploring / no set timeline"] },
      { id: "b5", text: "How would you prefer to receive your audit results?", type: "single", options: ["Email report with recommendations", "Live walkthrough with a security advisor", "Both — report + live consultation", "Just the score for now"] },
    ],
  },
];

const RISK_WEIGHTS = {
  p2: { "No active monitoring": 25, "Guards do periodic walkthroughs": 18, "Live camera monitoring by on-site staff": 12, "Remote video monitoring (3rd party SOC)": 8, "Hybrid (on-site + remote)": 5, "AI-assisted monitoring": 2 },
  p3: { "Under 30 seconds": 0, "30 seconds – 2 minutes": 4, "2–5 minutes": 10, "5–15 minutes": 18, "Over 15 minutes / inconsistent": 25, "We don't measure this": 22 },
  i1: { "0": 0, "1–3": 5, "4–10": 12, "11–25": 20, "25+": 28, "We don't track this": 22 },
  i3: { "Under $5,000": 2, "$5,000–$25,000": 6, "$25,000–$100,000": 14, "$100,000–$500,000": 22, "Over $500,000": 30, "Unknown / not measured": 18 },
  i4: { "Real-time alert from security system": 0, "Guard or employee witnesses it": 8, "Reviewing footage after the fact": 20, "Third-party report (police, customer, etc.)": 22, "Often goes undetected for hours or days": 30 },
  t4: { "Yes — actively deployed": 0, "Piloting / evaluating": 4, "Interested but haven't started": 10, "No — not a priority": 16, "Unsure what's available": 12 },
};

function getRiskScore(answers) {
  let score = 0;
  for (const [qId, weights] of Object.entries(RISK_WEIGHTS)) {
    const answer = answers[qId];
    if (answer && typeof answer === "string" && weights[answer] !== undefined) {
      score += weights[answer];
    }
  }
  const challenges = answers["p4"];
  if (Array.isArray(challenges)) score += challenges.length * 3;
  const incTypes = answers["i2"];
  if (Array.isArray(incTypes)) score += incTypes.length * 2;
  return Math.min(100, Math.max(0, score));
}

function getRiskLevel(score) {
  if (score <= 20) return { label: "Low Risk", color: "#2ECC40", message: "Your security posture is solid. Minor optimizations could yield efficiency gains." };
  if (score <= 40) return { label: "Moderate Risk", color: "#FFDC00", message: "Several gaps exist that are likely costing you money and leaving you exposed to preventable incidents." };
  if (score <= 65) return { label: "Elevated Risk", color: "#FF851B", message: "Significant vulnerabilities in your operation. You're likely overspending on labor while under-investing in proactive detection." };
  return { label: "Critical Risk", color: "#FF4136", message: "Your current security model has serious structural weaknesses. The cost of inaction is compounding — both financially and in terms of liability." };
}

function ProgressBar({ current, total }) {
  const pct = ((current + 1) / total) * 100;
  return (
    <div className="w-full h-1 bg-border rounded overflow-hidden">
      <div
        className="h-full rounded transition-all duration-400 ease-out"
        style={{ width: `${pct}%`, background: "linear-gradient(90deg, hsl(var(--accent-orange)), hsl(var(--primary)))" }}
      />
    </div>
  );
}

function QuestionCard({ question, answer, onAnswer, animKey }) {
  const isMulti = question.type === "multi";
  const selected = answer || (isMulti ? [] : "");

  const handleSelect = (opt) => {
    if (isMulti) {
      const arr = Array.isArray(selected) ? [...selected] : [];
      if (arr.includes(opt)) {
        onAnswer(arr.filter((o) => o !== opt));
      } else {
        if (question.maxSelect && arr.length >= question.maxSelect) return;
        onAnswer([...arr, opt]);
      }
    } else {
      onAnswer(opt);
    }
  };

  return (
    <div key={animKey}>
      <h3 className="text-xl font-bold text-foreground mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {question.text}
      </h3>
      {isMulti && (
        <p className="text-xs text-muted-foreground mb-5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Select all that apply{question.maxSelect ? ` (up to ${question.maxSelect})` : ""}
        </p>
      )}
      {!isMulti && <div className="mb-5" />}
      <div className={`grid gap-2.5 ${question.options.length > 6 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
        {question.options.map((opt) => {
          const isSelected = isMulti ? (Array.isArray(selected) && selected.includes(opt)) : selected === opt;
          const atMax = isMulti && question.maxSelect && Array.isArray(selected) && selected.length >= question.maxSelect && !isSelected;
          return (
            <button
              key={opt}
              onClick={() => handleSelect(opt)}
              disabled={atMax}
              className={`flex items-center gap-3 p-3.5 rounded-lg border text-left text-sm transition-all ${
                isSelected
                  ? 'border-primary bg-primary/10 text-foreground font-semibold'
                  : atMax
                    ? 'border-border bg-card text-muted-foreground opacity-40 cursor-not-allowed'
                    : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:bg-primary/5 cursor-pointer'
              }`}
            >
              <span className={`w-4.5 h-4.5 min-w-[18px] min-h-[18px] flex items-center justify-center border-2 transition-all ${
                isMulti ? 'rounded' : 'rounded-full'
              } ${isSelected ? 'border-primary bg-primary' : 'border-border'}`}>
                {isSelected && (
                  <span className="text-[10px] text-white font-bold">
                    {isMulti ? "✓" : "●"}
                  </span>
                )}
              </span>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ScoreGauge({ score }) {
  const risk = getRiskLevel(score);
  const angle = (score / 100) * 180;
  return (
    <div className="text-center mb-8">
      <svg width="240" height="140" viewBox="0 0 240 140">
        <path d="M 20 130 A 100 100 0 0 1 220 130" fill="none" stroke="hsl(var(--border))" strokeWidth="16" strokeLinecap="round" />
        <path
          d="M 20 130 A 100 100 0 0 1 220 130"
          fill="none"
          stroke={risk.color}
          strokeWidth="16"
          strokeLinecap="round"
          strokeDasharray={`${(angle / 180) * 314} 314`}
          style={{ transition: "stroke-dasharray 1.2s ease, stroke 0.5s ease", filter: `drop-shadow(0 0 8px ${risk.color}55)` }}
        />
        <text x="120" y="110" textAnchor="middle" fill={risk.color} fontSize="42" fontWeight="800" fontFamily="'Space Mono', monospace">{score}</text>
        <text x="120" y="132" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="12" fontFamily="'DM Sans', sans-serif">/100</text>
      </svg>
      <div className="text-xl font-extrabold mt-1" style={{ color: risk.color, fontFamily: "'Outfit', sans-serif" }}>{risk.label}</div>
    </div>
  );
}

function ResultsView({ answers, onRestart, onOpenContact }) {
  const score = getRiskScore(answers);
  const risk = getRiskLevel(score);

  const priorities = answers["b3"] || [];
  const challenges = answers["p4"] || [];
  const discoveryMethod = answers["i4"] || "Unknown";
  const monitoringModel = answers["p2"] || "Unknown";
  const timeline = answers["b4"] || "Unknown";
  const deliveryPref = answers["b5"] || "Email report with recommendations";

  return (
    <div>
      <div className="text-center mb-2">
        <span className="text-xs font-bold tracking-widest uppercase text-accent-orange" style={{ fontFamily: "'Space Mono', monospace" }}>
          AUDIT COMPLETE
        </span>
      </div>
      <h2 className="text-center text-3xl font-extrabold text-foreground mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Your Security Risk Score
      </h2>

      <ScoreGauge score={score} />

      <div className="rounded-xl p-5 mb-7 text-center border" style={{ borderColor: `${risk.color}33`, background: `${risk.color}08` }}>
        <p className="text-base text-muted-foreground leading-relaxed">{risk.message}</p>
      </div>

      {/* Key findings */}
      <div className="bg-card border border-border rounded-xl p-6 mb-5">
        <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-4" style={{ fontFamily: "'Space Mono', monospace" }}>
          KEY FINDINGS
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { label: "Monitoring Model", value: monitoringModel, icon: "📡" },
            { label: "Incident Discovery", value: discoveryMethod, icon: "🔍" },
            { label: "Timeline", value: timeline, icon: "⏱" },
            { label: "Delivery Preference", value: deliveryPref, icon: "📬" },
          ].map((item) => (
            <div key={item.label} className="p-3.5 bg-muted rounded-lg border border-border">
              <div className="text-xs text-muted-foreground mb-1.5">{item.icon} {item.label}</div>
              <div className="text-sm font-semibold text-foreground">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {challenges.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6 mb-5">
          <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3.5" style={{ fontFamily: "'Space Mono', monospace" }}>
            IDENTIFIED CHALLENGES
          </h3>
          <div className="flex flex-wrap gap-2">
            {challenges.map((c) => (
              <span key={c} className="px-3.5 py-2 bg-accent-orange/10 border border-accent-orange/20 rounded-lg text-xs text-accent-orange">
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {priorities.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3.5" style={{ fontFamily: "'Space Mono', monospace" }}>
            YOUR TOP PRIORITIES
          </h3>
          <div className="flex flex-col gap-2.5">
            {priorities.map((p, i) => (
              <div key={p} className="flex items-center gap-3.5 p-3 bg-muted rounded-lg border border-border">
                <span className="w-7 h-7 rounded-full flex items-center justify-center bg-accent-orange/15 text-accent-orange text-xs font-extrabold" style={{ fontFamily: "'Space Mono', monospace" }}>
                  {i + 1}
                </span>
                <span className="text-sm text-foreground">{p}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="flex gap-3.5 justify-center flex-wrap">
        <button
          onClick={onOpenContact}
          className="bg-accent-orange text-white border-none rounded-lg px-9 py-4 text-sm font-bold cursor-pointer transition-transform hover:scale-[1.02]"
          style={{ boxShadow: "0 4px 24px rgba(255,133,27,0.35)" }}
        >
          Get My Full Report &rarr;
        </button>
        <button
          onClick={onRestart}
          className="bg-transparent text-muted-foreground border border-border rounded-lg px-7 py-4 text-sm font-semibold cursor-pointer hover:border-foreground/25 hover:text-foreground transition-all"
        >
          Retake Audit
        </button>
      </div>
    </div>
  );
}

export function CTASection({ onOpenContact }) {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const totalQuestions = SECTIONS.reduce((sum, s) => sum + s.questions.length, 0);
  const flatIndex = SECTIONS.slice(0, sectionIdx).reduce((sum, s) => sum + s.questions.length, 0) + questionIdx;

  const section = SECTIONS[sectionIdx];
  const question = section?.questions[questionIdx];
  const currentAnswer = question ? answers[question.id] : undefined;
  const canAdvance = question?.type === "multi" ? (Array.isArray(currentAnswer) && currentAnswer.length > 0) : !!currentAnswer;

  const advance = () => {
    setTransitioning(true);
    setTimeout(() => {
      if (questionIdx < section.questions.length - 1) {
        setQuestionIdx(questionIdx + 1);
      } else if (sectionIdx < SECTIONS.length - 1) {
        setSectionIdx(sectionIdx + 1);
        setQuestionIdx(0);
      } else {
        setShowResults(true);
      }
      setTransitioning(false);
    }, 200);
  };

  const goBack = () => {
    setTransitioning(true);
    setTimeout(() => {
      if (questionIdx > 0) {
        setQuestionIdx(questionIdx - 1);
      } else if (sectionIdx > 0) {
        const prevSection = SECTIONS[sectionIdx - 1];
        setSectionIdx(sectionIdx - 1);
        setQuestionIdx(prevSection.questions.length - 1);
      }
      setTransitioning(false);
    }, 200);
  };

  const restart = () => {
    setAnswers({});
    setSectionIdx(0);
    setQuestionIdx(0);
    setShowResults(false);
  };

  return (
    <div id="audit" className="py-16 md:py-24 bg-background">
      <div className="max-w-[680px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-9">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-2.5 h-2.5 rounded-full bg-accent-orange animate-pulse" style={{ boxShadow: "0 0 12px hsl(var(--accent-orange) / 0.6)" }} />
            <span className="text-xs font-bold tracking-widest uppercase text-accent-orange" style={{ fontFamily: "'Space Mono', monospace" }}>
              SECURITY AUDIT
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {showResults ? "Your Security Assessment" : "Custom Security Audit"}
          </h2>
          {!showResults && (
            <p className="text-base text-muted-foreground">
              25 questions. 5 minutes. A complete picture of your security risk exposure.
            </p>
          )}
        </div>

        {!showResults ? (
          <>
            {/* Section tabs */}
            <div className="flex gap-1 mb-1.5 overflow-x-auto">
              {SECTIONS.map((s, i) => {
                const isActive = i === sectionIdx;
                const isComplete = i < sectionIdx;
                return (
                  <div key={s.id} className={`flex-1 p-2.5 rounded-lg text-center transition-all min-w-[100px] border ${
                    isActive ? 'bg-accent-orange/5 border-accent-orange/25' : 'bg-card border-border'
                  }`}>
                    <div className="text-base mb-0.5">{s.icon}</div>
                    <div className={`text-[10px] font-bold tracking-wide uppercase ${
                      isActive ? 'text-accent-orange' : isComplete ? 'text-green-500' : 'text-muted-foreground'
                    }`}>
                      {isComplete ? "✓ " : ""}{s.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Progress */}
            <div className="mb-8">
              <ProgressBar current={flatIndex} total={totalQuestions} />
              <div className="flex justify-between mt-2">
                <span className="text-[11px] text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
                  Question {flatIndex + 1} of {totalQuestions}
                </span>
                <span className="text-[11px] text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
                  {Math.round(((flatIndex + 1) / totalQuestions) * 100)}%
                </span>
              </div>
            </div>

            {/* Question */}
            <div className={`bg-card border border-border rounded-2xl p-7 min-h-[320px] transition-opacity ${transitioning ? 'opacity-30' : 'opacity-100'}`}>
              {question && (
                <QuestionCard
                  question={question}
                  answer={currentAnswer}
                  onAnswer={(val) => setAnswers({ ...answers, [question.id]: val })}
                  animKey={question.id}
                />
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              <button
                onClick={goBack}
                disabled={sectionIdx === 0 && questionIdx === 0}
                className={`bg-transparent border border-border rounded-lg px-6 py-3 text-sm font-semibold transition-all ${
                  sectionIdx === 0 && questionIdx === 0
                    ? 'text-muted-foreground/40 cursor-not-allowed'
                    : 'text-muted-foreground hover:text-foreground hover:border-foreground/25 cursor-pointer'
                }`}
              >
                &larr; Back
              </button>
              <button
                onClick={advance}
                disabled={!canAdvance}
                className={`border-none rounded-lg px-8 py-3 text-sm font-bold transition-all ${
                  canAdvance
                    ? 'bg-accent-orange text-white cursor-pointer hover:scale-[1.02]'
                    : 'bg-muted text-muted-foreground/40 cursor-not-allowed'
                }`}
                style={canAdvance ? { boxShadow: "0 4px 18px hsl(var(--accent-orange) / 0.3)" } : {}}
              >
                {sectionIdx === SECTIONS.length - 1 && questionIdx === section.questions.length - 1 ? "See Results →" : "Next →"}
              </button>
            </div>
          </>
        ) : (
          <ResultsView answers={answers} onRestart={restart} onOpenContact={onOpenContact} />
        )}
      </div>
    </div>
  );
}
